import React from 'react';
import { Calendar } from 'lucide-react';
import { themeForCategory } from '@/lib/eventCategoryTheme';

/**
 * Phase 26.9 — Event cover image display.
 *
 * Renders one of three states, at a 16:9 aspect ratio:
 *   1. Loading (nothing rendered until we know the outcome).
 *   2. Cover present  → <img> with lazy loading + object-cover.
 *   3. No cover       → branded KR default placeholder with event title.
 *
 * Props:
 *   coverUrl   string   Fully-qualified or relative URL to the cover
 *                       image (as emitted by the backend). Optional.
 *   title      string   Event title, shown on the default placeholder.
 *   variant    'hero' | 'thumb'
 *                       'hero' → full-width 16:9 hero banner (public
 *                                event page, drawer top).
 *                       'thumb'→ compact 16:9 card thumbnail (list rows).
 *   className  string   Extra utility classes for the wrapper.
 *
 * The component is intentionally stateless — the parent decides when
 * to bust cache (e.g. after replacing the image) by appending a query
 * string to `coverUrl` (`?v=<timestamp>`).
 */
export default function EventCover({
  coverUrl,
  title = 'Event',
  category = null,
  variant = 'hero',
  showTitleOnPlaceholder = true,
  className = '',
}) {
  const [failed, setFailed] = React.useState(false);
  React.useEffect(() => { setFailed(false); }, [coverUrl]);

  const showImg = !!coverUrl && !failed;
  const isThumb = variant === 'thumb';

  return (
    <div
      className={`relative w-full overflow-hidden ${
        isThumb ? 'rounded-md border border-slate-200' : 'rounded-xl'
      } ${className}`}
      style={{ aspectRatio: '16 / 9' }}
      data-testid="event-cover"
    >
      {showImg ? (
        <img
          src={coverUrl}
          alt={`Cover for ${title}`}
          loading="lazy"
          onError={() => setFailed(true)}
          className="absolute inset-0 h-full w-full object-cover"
          data-testid="event-cover-image"
        />
      ) : (
        <DefaultCover
          title={title}
          isThumb={isThumb}
          showTitle={showTitleOnPlaceholder}
          category={category}
        />
      )}
    </div>
  );
}

/**
 * Branded KR Enterprise Group placeholder. Category drives the gradient
 * so training/certification/networking/etc. each read as distinct at a
 * glance without needing an uploaded image.
 */
function DefaultCover({ title, isThumb, showTitle = true, category = null }) {
  const theme = themeForCategory(category);
  const gradient = `linear-gradient(135deg, ${theme.from} 0%, ${theme.to} 100%)`;
  return (
    <div
      className="absolute inset-0 flex flex-col justify-between overflow-hidden"
      style={{ background: gradient }}
      data-testid="event-cover-placeholder"
      data-category={category || 'default'}
    >
      {/* decorative rings — pure CSS, no assets */}
      <div
        aria-hidden
        className="absolute -right-24 -top-24 h-64 w-64 rounded-full"
        style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.10), transparent 70%)' }}
      />
      <div
        aria-hidden
        className="absolute -left-16 -bottom-24 h-56 w-56 rounded-full"
        style={{ background: `radial-gradient(circle, ${theme.accent}22, transparent 70%)` }}
      />

      <div className={`relative flex items-center gap-2 ${isThumb ? 'p-2 text-[10px]' : 'p-5 text-xs'}`} style={{ color: theme.accent }}>
        <Calendar size={isThumb ? 12 : 14} />
        <span className="font-semibold uppercase tracking-[0.18em]">
          KR Enterprise Group
        </span>
      </div>

      <div className={`relative ${isThumb ? 'px-3 pb-3' : 'px-6 pb-6'}`}>
        {showTitle && (
          <div
            className={`text-white font-semibold leading-tight ${
              isThumb ? 'text-sm line-clamp-2' : 'text-2xl md:text-3xl line-clamp-3'
            }`}
          >
            {title}
          </div>
        )}
        {!isThumb && showTitle && (
          <div className="mt-1 text-xs text-white/70 tracking-wide">
            {theme.label} · KR Enterprise Group
          </div>
        )}
      </div>
    </div>
  );
}
